/*======================================================================================================================
  * Bluebell: src/app/app.component.ts
  * Last Edited: 22/08/2024
  * Copyright 2016-2024 Bramble CFD LTD
  * The contents of this file are NOT for redistribution
  * See AUTHORS for list of developers on this project
 =====================================================================================================================*/
import { Component, computed, effect, inject } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { LoadingSpinnerComponent } from '@components/loading-spinner/loading-spinner.component';
import { Store } from '@ngrx/store';
import { DARK_MODE_CLASS } from '@app/primeng-config';
import { ColorSchemes, User, UserSettings } from '@state/account/account.interfaces';
import { selectUser, selectUserSettings } from '@state/account/account.selectors';
import { selectAccessToken } from '@state/authentication/authentication.selectors';
import { AuthenticationService } from '@state/authentication/authentication.service';
import { ToastModule } from 'primeng/toast';


@Component({
  selector: 'bb-root',
  templateUrl: './app.component.html',
  // providers: [MessageService],
  imports: [RouterOutlet, ToastModule, LoadingSpinnerComponent],
})
export class AppComponent {
  #store = inject(Store);
  #auth = inject(AuthenticationService);

  userProfile = this.#store.selectSignal<User | null>(selectUser);
  userSettings = this.#store.selectSignal<UserSettings | null>(selectUserSettings);
  accessToken = this.#store.selectSignal<string | null>(selectAccessToken);

  protected switchInProgress = this.#auth.accountSwitchInProgress;

  colorScheme = computed(() => this.userSettings()?.color_scheme);

  onColorSchemeChange = effect(() => {
    switch (this.colorScheme()) {
      case ColorSchemes.dark:
        this.applyColorScheme(true);
        break;
      case ColorSchemes.light:
        this.applyColorScheme(false);
        break;
      case ColorSchemes.auto:
        if (window.matchMedia) {
          const mediaQuery = '(prefers-color-scheme: dark)';
          this.applyColorScheme(window.matchMedia(mediaQuery).matches);
          window.matchMedia(mediaQuery).addEventListener('change', e => this.applyColorScheme(e.matches));
        }
        break;
    }
  });

  constructor() {

    // Hide the preloader only when the user profile and user settings have been loaded into the Store,
    // if there is an access token for the user. If the user settings or user profile get cleared while
    // there is an access token, show the preloader again.
    effect(() => {
      if (!this.accessToken() || (
        this.userProfile() && this.userSettings()
      )) {
        const element = document.getElementById('preloader')
        if (element) {
          element.style.display = 'none';  // hide preloader
        }
      } else {
        const element = document.getElementById('preloader')
        if (element) {
          element.style.display = 'block';
        }
      }
    });

  }

  applyColorScheme(darkMode: boolean): void {
    const html = document.documentElement;
    if (darkMode && this.userSettings()?.beta_tester) {
      html.classList.add(DARK_MODE_CLASS);
    } else {
      html.classList.remove(DARK_MODE_CLASS);
    }
  }

}
